'use client';

import {
  Activity, BarChart3, Building2, CalendarDays, Languages, LayoutDashboard,
  LogOut, Menu, Moon, ShieldCheck, Sun, Swords, Trophy, Users, X,
} from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { useAuth } from '@/auth/auth-provider';
import { usePreferences } from '@/components/preferences-provider';
import { SessionProvider } from '@/components/session-context';
import type { MessageKey } from '@/lib/i18n';
import type { AdminRole, SessionUser } from '@/types';

const nav: { href: string; label: MessageKey; icon: typeof Activity; roles?: AdminRole[] }[] = [
  { href: '/', label: 'overview', icon: LayoutDashboard },
  { href: '/users', label: 'users', icon: ShieldCheck, roles: ['MAIN_ADMIN'] },
  { href: '/teams', label: 'teams', icon: Users },
  { href: '/players', label: 'players', icon: Activity },
  { href: '/tournaments', label: 'tournaments', icon: Trophy },
  { href: '/matches', label: 'matches', icon: Swords },
  { href: '/analytics', label: 'analytics', icon: BarChart3 },
  { href: '/venues', label: 'venues', icon: Building2 },
  { href: '/settings', label: 'settings', icon: CalendarDays },
];

export function AppShell({ user, children }: { user: SessionUser; children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { language, setLanguage, theme, setTheme, t } = usePreferences();
  const [open, setOpen] = useState(false);
  const allowed = nav.filter((item) => !item.roles || item.roles.includes(user.role));
  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return <SessionProvider user={user}><div className="app-shell">
    <aside className={`sidebar ${open ? 'open' : ''}`}>
      <div className="brand"><img src="/futsaly-mark.svg" alt="" width={36} height={36} /><span>Futsaly Admin</span><button className="button icon mobile-menu" onClick={() => setOpen(false)}><X size={20} /></button></div>
      <nav className="nav">
        {allowed.map(({ href, label, icon: Icon }) => {
          const active = href === '/' ? pathname === '/' : pathname.startsWith(href);
          return <NavLink key={href} to={href} onClick={() => setOpen(false)} className={`nav-link ${active ? 'active' : ''}`}><Icon size={19} /><span>{t(label)}</span></NavLink>;
        })}
      </nav>
      <div className="sidebar-footer">
        <div style={{ padding: '10px 12px' }}><strong style={{ display: 'block', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</strong><span className="badge" style={{ marginTop: 7 }}>{user.role.replaceAll('_', ' ')}</span></div>
        <button className="nav-link" onClick={signOut}><LogOut size={19} />{t('logout')}</button>
      </div>
    </aside>
    <main className="shell-main">
      <header className="topbar">
        <div className="topbar-actions"><button className="button icon mobile-menu" onClick={() => setOpen(true)} aria-label="Open navigation"><Menu /></button><strong>{t('adminPanel')}</strong></div>
        <div className="topbar-actions">
          <button className="button secondary" onClick={() => setLanguage(language === 'en' ? 'fa' : language === 'fa' ? 'ps' : 'en')}><Languages size={17} />{language.toUpperCase()}</button>
          <button className="button icon" aria-label="Change theme" onClick={() => setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light')}>{theme === 'dark' ? <Moon size={19} /> : <Sun size={19} />}</button>
        </div>
      </header>
      {children}
    </main>
  </div></SessionProvider>;
}
